import classNames from 'classnames';
import uniqueId from 'lodash/uniqueId';
import * as React from 'react';
import { PageSection } from '~/Features/Layouts/Components/PageSection/PageSection';
import { PageSectionHeading } from '~/Features/Layouts/Components/PageSectionHeading/PageSectionHeading';
import { PillTag } from '~/Features/Partials/PillTag/PillTag';
import type { HtmlString } from '~/Features/Types/HtmlString.csharp';
import { List } from '~/Partials/List/List';
import type { IconName } from '~/Partials/SvgIcon/SvgIcon';
import { SvgIcon } from '~/Partials/SvgIcon/SvgIcon';
import type { StrictOmit } from '~/Partials/Utils/typeUtils';
import type { FactCardProps } from './FactCard/FactCard';
import { FactCard } from './FactCard/FactCard';
import styles from './FactsSection.module.css';

export interface Tag {
    title: string;
    value: string;
    iconName: IconName;
}

export interface FactsSectionProps {
    id: string;
    facts: StrictOmit<FactCardProps, 'theme'>[];
    tags: Tag[];
    title?: HtmlString;
}

export const FactsSection: React.FC<FactsSectionProps> = props => {
    const { id, title, facts, tags } = props;

    const hasFacts = facts.length !== 0;
    const hasTags = tags.length !== 0;

    return (
        <PageSection id={id} className={styles.FactsSection}>
            {title && <PageSectionHeading className={styles.Title} heading={title} />}
            {hasFacts && (
                <List
                    className={classNames(styles.Facts, hasTags && styles['Facts--withSeparator'])}
                    items={facts}
                    renderItem={fact => {
                        const cardId = uniqueId(fact.title?.value);

                        return (
                            <li key={cardId} className={styles.ListItem}>
                                <FactCard {...fact} />
                            </li>
                        );
                    }}
                />
            )}
            {hasTags && (
                <List
                    className={styles.Tags}
                    items={tags}
                    renderItem={tag => {
                        const tagId = uniqueId(tag.title);

                        return (
                            <li key={tagId}>
                                <PillTag type="solid" variant="dark" fontColor="light" title={tag.title}>
                                    <SvgIcon iconName={tag.iconName} />
                                    <span className={styles.TagInfo}>{tag.value}</span>
                                </PillTag>
                            </li>
                        );
                    }}
                />
            )}
        </PageSection>
    );
};
